import { createContext, forwardRef, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { toLatinDigits, toPersianDigits } from '@metal/shared'
import { cx } from '../cx'
import { Icon } from '../Icon'

/** Vite's development flag, read through a cast: this package loads no Vite types. */
const DEV = Boolean((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV)

/**
 * The label over a control.
 *
 * Required is the default state of a field, so it carries no mark: the few
 * fields a person may leave empty say so with «(اختیاری)». `required` is
 * still accepted so older call sites compile; it draws nothing.
 */
export function Label({
  required: _required,
  optional,
  className,
  children,
  ...rest
}: LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean; optional?: boolean }) {
  return (
    <label className={cx('label', className)} {...rest}>
      {children}
      {optional && <span className="label-opt">(اختیاری)</span>}
    </label>
  )
}

export interface FieldProps {
  label?: ReactNode
  /**
   * The control is announced as required (`aria-required`). Nothing is drawn:
   * a required field is the default and shows no asterisk.
   */
  required?: boolean
  /** Draws a muted «(اختیاری)» after the label. Only for fields a person may leave empty. */
  optional?: boolean
  /** Carries data the user needs. Never an explanation of why the field exists. */
  hint?: ReactNode
  /**
   * A short definition of a trade term («حداقل قابل خرید», «ضریب وزنی»),
   * shown only on hover or focus of the «i» after the label, so the form
   * itself carries no explanation.
   */
  tip?: string
  error?: string
  htmlFor?: string
  className?: string
  children: ReactNode
}

/**
 * What a field hands its control, rather than asking every caller for it.
 *
 * A field draws its label above its control as a sibling, so the two are
 * tied by `for` and `id` or not at all, and a control read out as «متن» is a
 * form nobody can fill in without seeing it. Every caller would otherwise
 * have to mint an id and pass it twice, which is the kind of thing that gets
 * left out; the context makes the association the default and leaves the
 * explicit `htmlFor` for the cases that need to name it. The same channel
 * carries `required` (read out, never drawn) and the id of the line under
 * the control, so the hint or the error is read with the control.
 */
export interface FieldContextValue {
  id: string
  required?: boolean
  /** The id of the hint or error line under the control, when one is drawn. */
  describedBy?: string
  /**
   * The field draws an error line. A control without an `invalid` of its own
   * (a `Check`) marks itself invalid from this.
   */
  invalid?: boolean
}

const FieldContext = createContext<FieldContextValue | undefined>(undefined)

/** The field a control sits in, if any. Custom controls read their id and aria from it. */
export function useFieldContext(): FieldContextValue | undefined {
  return useContext(FieldContext)
}

/** The control's own id, or the one its field minted. */
export function useFieldId(explicit?: string): string | undefined {
  const inherited = useContext(FieldContext)
  return explicit ?? inherited?.id
}

/**
 * The aria a control takes from its field: `aria-required` and
 * `aria-describedby`. Spread it before the caller's own props, so an
 * explicit attribute still wins.
 */
export function useFieldAria(): { 'aria-required'?: true; 'aria-describedby'?: string } {
  const ctx = useContext(FieldContext)
  if (!ctx) return {}
  return {
    ...(ctx.required ? { 'aria-required': true as const } : {}),
    ...(ctx.describedBy ? { 'aria-describedby': ctx.describedBy } : {}),
  }
}

export function Field({
  label,
  required,
  optional,
  hint,
  tip,
  error,
  htmlFor,
  className,
  children,
}: FieldProps) {
  const auto = useId()
  const id = htmlFor ?? auto
  const hasMessage = Boolean(error || hint)
  const hasError = Boolean(error)
  const messageId = `${id}-msg`
  const ctx = useMemo<FieldContextValue>(
    () => ({ id, required, describedBy: hasMessage ? messageId : undefined, invalid: hasError || undefined }),
    [id, required, hasMessage, hasError, messageId],
  )
  const labelEl = label ? (
    <Label htmlFor={id} optional={optional}>
      {label}
    </Label>
  ) : null
  return (
    <div className={cx('field', className)}>
      {labelEl &&
        (tip ? (
          <span className="label-row">
            {labelEl}
            <InfoTip text={tip} />
          </span>
        ) : (
          labelEl
        ))}
      <FieldContext.Provider value={ctx}>{children}</FieldContext.Provider>
      {error ? (
        <ErrorText id={messageId}>{error}</ErrorText>
      ) : hint ? (
        <Hint id={messageId}>{hint}</Hint>
      ) : null}
    </div>
  )
}

/**
 * The «i» that defines a term. The bubble opens on hover, on keyboard focus
 * and on a tap, which is the only way in on a phone; a second tap, a tap
 * elsewhere or Escape closes it. The button names it for a screen reader, so
 * the definition never takes room on the form.
 */
export function InfoTip({ text, className }: { text: string; className?: string }) {
  const id = useId()
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])
  return (
    <span ref={ref} className={cx('infotip', open && 'is-open', className)}>
      <button
        type="button"
        className="infotip-btn"
        aria-describedby={id}
        aria-label="توضیح"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <Icon name="info" size="sm" />
      </button>
      <span role="tooltip" id={id} className="infotip-bubble">
        {text}
      </span>
    </span>
  )
}

export function Hint({
  tone = 'muted',
  className,
  id,
  children,
}: {
  tone?: 'muted' | 'buy' | 'sell'
  className?: string
  id?: string
  children: ReactNode
}) {
  return (
    <div id={id} className={cx('hint', tone === 'buy' && 't-buy', tone === 'sell' && 't-sell', className)}>
      {children}
    </div>
  )
}

export function ErrorText({
  children,
  className,
  id,
}: {
  children: ReactNode
  className?: string
  id?: string
}) {
  return (
    <div id={id} className={cx('err', className)} role="alert">
      <Icon name="alert" size="sm" />
      {children}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  /**
   * This field carries a number. Two things follow from that, and this is the
   * only place either is written:
   *
   *   tabular figures with a slashed zero, so a column never reflows
   *   the value is Latin digits
   *
   * A Persian or Arabic digit typed or pasted into the box is rewritten to
   * Latin, along with the Persian decimal mark and thousands mark, before any
   * handler sees it. The field's value is what the API stores and what every
   * `\d` in a handler has to match. What the person sees is still Persian:
   * the digit face draws the Latin figures, and the point, as Persian ones.
   */
  numeric?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, numeric, className, onChange, id, ...rest },
  ref,
) {
  const fieldId = useFieldId(id)
  const aria = useFieldAria()
  return (
    <input
      ref={ref}
      id={fieldId}
      className={cx('input', numeric && 'num', invalid && 'is-error', className)}
      aria-invalid={invalid || undefined}
      {...aria}
      {...rest}
      onChange={
        numeric && onChange
          ? (e) => {
              const latin = toLatinDigits(e.target.value)
              if (latin !== e.target.value) e.target.value = latin
              onChange(e)
            }
          : onChange
      }
    />
  )
})

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  invalid?: boolean
  options: SelectOption[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, options, placeholder, className, id, ...rest },
  ref,
) {
  const fieldId = useFieldId(id)
  const aria = useFieldAria()
  return (
    <div className="select-wrap">
      <select
        ref={ref}
        id={fieldId}
        className={cx('select', invalid && 'is-error', className)}
        aria-invalid={invalid || undefined}
        {...aria}
        {...rest}
      >
        {/* The open list is drawn by the system, not with the page's digit
            face, so its figures are written Persian here. The values stay
            Latin. */}
        {placeholder && (
          <option value="" disabled>
            {toPersianDigits(placeholder)}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {toPersianDigits(o.label)}
          </option>
        ))}
      </select>
      <Icon name="chevron-down" size="sm" />
    </div>
  )
})

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ invalid, className, id, ...rest }, ref) {
  const fieldId = useFieldId(id)
  const aria = useFieldAria()
  return (
    <textarea
      ref={ref}
      id={fieldId}
      className={cx('textarea', invalid && 'is-error', className)}
      aria-invalid={invalid || undefined}
      {...aria}
      {...rest}
    />
  )
})

/** The unit lives inside the control, never beside it: ریال, کیلوگرم, درصد. */
export function Affix({ tag, children }: { tag: string; children: ReactElement }) {
  return (
    <div className="affix">
      {children}
      <span className="affix-tag">{tag}</span>
    </div>
  )
}

/**
 * One tick box. Inside a `Field` it takes the field's id, `aria-required`,
 * the line under it (`aria-describedby`) and, when the field draws an error,
 * `aria-invalid`, so a refused tick is read with its sentence and a refused
 * submit can focus it. One `Check` per `Field`, and nothing beside it: a set
 * of ticks is a `role="group"` named by its heading, not a field. A second
 * control in the same `Field` is warned about in development.
 */
export function Check({
  checked,
  onChange,
  disabled,
  invalid,
  id,
  children,
  className,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  /** Marks the tick refused. Inside a `Field`, its error line already does. */
  invalid?: boolean
  id?: string
  children?: ReactNode
  className?: string
}) {
  const field = useFieldContext()
  const inputId = useFieldId(id)
  const aria = useFieldAria()
  const refused = invalid ?? field?.invalid ?? false
  const input = useRef<HTMLInputElement>(null)
  const inheritedId = field?.id
  // Two controls in one Field share its id and its error. Said in
  // development only, where the screen that does it is being written.
  useEffect(() => {
    if (!DEV || id || !inheritedId) return
    const own = input.current?.id
    if (own && document.querySelectorAll(`[id="${CSS.escape(own)}"]`).length > 1) {
      console.warn(
        `Check: its Field's id "${own}" is used by another control. Put one Check in a Field; a set of ticks is a role="group".`,
      )
    }
  }, [id, inheritedId])
  return (
    <label
      className={cx('check', checked && 'is-on', refused && 'is-error', className)}
      aria-disabled={disabled || undefined}
    >
      <input
        ref={input}
        type="checkbox"
        id={inputId}
        className="visually-hidden"
        checked={checked}
        disabled={disabled}
        aria-invalid={refused || undefined}
        {...aria}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="box">
        <Icon name="check" />
      </span>
      {children}
    </label>
  )
}

/**
 * One choice of a radio group. A radio takes no `aria-invalid` (ARIA keeps
 * it for the group), so the refusal lives on the wrapper: `role="radiogroup"`
 * with `aria-invalid`, `aria-describedby` pointing at the error line, and
 * `data-field="<key>"`. A refused submit then focuses the chosen radio, or
 * the first one.
 */
export function Radio({
  checked,
  onChange,
  name,
  value,
  disabled,
  children,
  className,
}: {
  checked: boolean
  onChange: (v: string) => void
  name: string
  value: string
  disabled?: boolean
  children?: ReactNode
  className?: string
}) {
  return (
    <label
      className={cx('radio', checked && 'is-on', className)}
      aria-disabled={disabled || undefined}
    >
      <input
        type="radio"
        className="visually-hidden"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span className="box" />
      {children}
    </label>
  )
}

export function Switch({
  checked,
  onChange,
  disabled,
  label,
  className,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  label: string
  className?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx('switch', checked && 'is-on', className)}
      onClick={() => onChange(!checked)}
    />
  )
}

/**
 * The six box code. ID-032 fixes six digits and a 120 second expiry.
 *
 * The boxes are the state, not a view of the parent's string. Clearing the
 * third box has to leave the fourth where it is: deriving the boxes from a
 * compact string means the code closes the gap, the digits after it slide one
 * place left, and somebody correcting a single mistyped digit watches the
 * rest of their code rearrange itself.
 *
 * What the parent is told is the digits that are actually filled, so a code
 * with a hole in it is short and the submit button stays shut until the hole
 * is filled.
 *
 * A whole code arrives three ways and all three land in every box: pasted
 * into any box, typed or autofilled into one box (the SMS autofill writes the
 * full code into the first box, which is why that box alone carries
 * `autoComplete="one-time-code"` and none of them a one character limit).
 */
export function Otp({
  length = 6,
  value,
  onChange,
  invalid,
  autoFocus,
  ariaLabel = 'کد تأیید',
}: {
  length?: number
  value: string
  onChange: (v: string) => void
  invalid?: boolean
  autoFocus?: boolean
  /** Names the group of boxes for a screen reader. */
  ariaLabel?: string
}) {
  const id = useId()
  const [slots, setSlots] = useState<string[]>(() => spread(value, length))

  // The parent clears the code after a wrong one, and a resend mints a new
  // challenge. Either way the boxes follow, and only when the two have
  // actually diverged, so typing is never fought by this.
  const emitted = slots.filter(Boolean).join('')
  if (value !== emitted) {
    setSlots(spread(value, length))
  }

  const write = (next: string[]) => {
    setSlots(next)
    onChange(next.filter(Boolean).join(''))
  }

  const boxAt = (el: HTMLElement, index: number): HTMLInputElement | undefined =>
    el.parentElement?.children[index] as HTMLInputElement | undefined

  /**
   * Several digits at once, from box `from` on. A full code always starts at
   * the first box, wherever it was put. The caret lands after the last digit
   * written, or on the last box.
   */
  const fill = (el: HTMLElement, digits: string, from: number) => {
    const start = digits.length >= length ? 0 : from
    const next = [...slots]
    const chars = digits.slice(0, length - start)
    for (let k = 0; k < chars.length; k += 1) next[start + k] = chars[k]
    write(next)
    boxAt(el, Math.min(length - 1, start + chars.length))?.focus()
  }

  return (
    <div className={cx('otp', invalid && 'is-error')} dir="ltr" role="group" aria-label={ariaLabel}>
      {slots.map((d, i) => (
        <input
          key={`${id}-${i}`}
          className={cx('otp-box', d && 'is-on')}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && i === 0}
          value={d}
          aria-label={`رقم ${toPersianDigits(i + 1)}`}
          aria-invalid={invalid || undefined}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const digits = toLatinDigits(e.target.value).replace(/\D/g, '')
            // One new digit typed beside the one a box already held.
            const besideOld = Boolean(d) && digits.length === 2 && digits.includes(d)
            if (digits.length > 1 && !besideOld) {
              fill(e.target, digits, i)
              return
            }
            // A box keeps the new digit, whichever side of the old one the
            // caret was on.
            const raw = besideOld ? (digits[0] === d ? digits[1] : digits[0]) : digits
            write(slots.map((slot, n) => (n === i ? raw : slot)))
            if (raw) boxAt(e.target, i + 1)?.focus()
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Backspace') return
            // A filled box empties in place. An empty one hands the backspace
            // to the box before it, which is where the caret looks to be.
            if (d) return
            e.preventDefault()
            write(slots.map((slot, n) => (n === i - 1 ? '' : slot)))
            boxAt(e.target as HTMLElement, i - 1)?.focus()
          }}
          onPaste={(e) => {
            e.preventDefault()
            const pasted = toLatinDigits(e.clipboardData.getData('text')).replace(/\D/g, '')
            if (pasted) fill(e.currentTarget, pasted, i)
          }}
        />
      ))}
    </div>
  )
}

/** A code as one box per character, padded to the full width. */
function spread(code: string, length: number): string[] {
  return Array.from({ length }, (_, i) => code[i] ?? '')
}
